// src/app/shared/utils/voice-punctuation.util.ts
//
// Converts EXPLICITLY SPOKEN punctuation commands ("comma", "full stop",
// "question mark", ...) into their punctuation marks, for recognition
// engines that transcribe the spoken word instead of inserting the mark
// itself.
//
// This is NOT inferred punctuation. It never guesses a mark from silence,
// sentence shape, or intonation — it only fires on the literal command
// words a user speaks on purpose, and only when using that word is
// genuinely more likely to be a command than a word being used naturally.
//
// Legal-text safety note: "period" is deliberately EXCLUDED from
// single-word commands. Legal/consumer questionnaires routinely contain
// phrases like "notice period", "trial period", "probation period",
// "cooling-off period" and "limitation period" — converting a bare
// "period" would silently corrupt exactly the kind of legal terminology
// this app must preserve. "full stop" is unambiguous and is supported for
// the same mark instead.
//
// Safety heuristic: a command phrase preceded by a determiner/article
// ("the question mark", "a comma") is talking ABOUT the punctuation mark,
// not asking to insert one, so those occurrences are left as literal words.
// This directly covers cases like "I asked him for the question mark",
// which must NOT become "I asked him for the ?".

interface SpokenPunctuationRule {
  /** Lower-case words of the spoken phrase, already split. */
  words: string[];
  mark: string;
}

// Multi-word phrases are listed so they're matched whole; order does not
// otherwise matter since matching below always tries the longest phrase
// starting at each position first.
const RULES: SpokenPunctuationRule[] = [
  { words: ['exclamation', 'mark'], mark: '!' },
  { words: ['exclamation', 'point'], mark: '!' },
  { words: ['question', 'mark'], mark: '?' },
  { words: ['full', 'stop'], mark: '.' },
  { words: ['new', 'paragraph'], mark: '\n\n' },
  { words: ['new', 'line'], mark: '\n' },
  { words: ['semicolon'], mark: ';' },
  { words: ['colon'], mark: ':' },
  { words: ['comma'], mark: ',' },
];

const DETERMINERS = new Set([
  'a', 'an', 'the', 'this', 'that', 'these', 'those',
  'my', 'your', 'his', 'her', 'its', 'our', 'their', 'whose'
]);

// Longest phrase first, so "exclamation mark" is tried before any
// hypothetical single-word rule could shadow it.
const RULES_BY_LENGTH_DESC = [...RULES].sort((a, b) => b.words.length - a.words.length);

/**
 * Applies spoken-punctuation commands to `text`. Pure and idempotent:
 * running it again on already-converted text has no further effect, since
 * the command words themselves are consumed (replaced) on the first pass —
 * there is no punctuation-word left for a second pass to act on.
 *
 * Only ever replaces command words with a mark or leaves them untouched as
 * literal words; never removes, reorders, or rewrites anything else in the
 * text.
 */
export function applySpokenPunctuationCommands(text: string): string {
  if (!text) { return text; }

  const words = text.split(/\s+/).filter(Boolean);
  const out: string[] = [];

  let i = 0;
  while (i < words.length) {
    const rule = matchRuleAt(words, i);

    if (!rule) {
      out.push(words[i]);
      i++;
      continue;
    }

    const prevWord = out.length ? stripPunctuation(out[out.length - 1]).toLowerCase() : '';
    if (DETERMINERS.has(prevWord)) {
      // Talking about the mark ("the question mark"), not commanding it —
      // keep the literal words exactly as spoken.
      for (let w = 0; w < rule.words.length; w++) {
        out.push(words[i + w]);
      }
    } else if (rule.mark === '\n' || rule.mark === '\n\n') {
      out.push(rule.mark);
    } else if (out.length) {
      // Attach the mark to the previous word, no space before it — normal
      // punctuation spacing.
      out[out.length - 1] = out[out.length - 1] + rule.mark;
    } else {
      // Command was the very first thing spoken in this session — nothing
      // to attach it to; drop the command rather than lead with punctuation.
    }

    i += rule.words.length;
  }

  return out
    .join(' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')     // no stray spaces around inserted newlines
    .replace(/[ \t]+([,.;:!?])/g, '$1')   // no space before an attached mark
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function matchRuleAt(words: string[], index: number): SpokenPunctuationRule | null {
  for (const rule of RULES_BY_LENGTH_DESC) {
    const len = rule.words.length;
    if (index + len > words.length) { continue; }
    let matches = true;
    for (let w = 0; w < len; w++) {
      if (stripPunctuation(words[index + w]).toLowerCase() !== rule.words[w]) {
        matches = false;
        break;
      }
    }
    if (matches) { return rule; }
  }
  return null;
}

function stripPunctuation(word: string): string {
  return word.replace(/^[^\p{L}\d]+|[^\p{L}\d]+$/gu, '');
}